import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAppSelector, useAppDispatch } from '../hooks/useAppDispatch'
import { useGetOrCreateConversationMutation, useSendMessageMutation, useGetConversationsQuery, useGetChatAgentsQuery, useGetChatCategoriesQuery, useGetAgentByIdQuery, useCreateMessageUnlockOrderMutation, useUnlockMessageMutation, useUnlockMessageFreeMutation, chatApi } from '../store/api/chatApi'
import { useGetAiUsageQuery } from '../store/api/usersApi'
import { useGetSpendingCapConfigQuery } from '../store/api/agentsApi'
import { MultiSelectDropdown } from '../components/ui/MultiSelectDropdown'
import { SearchInput, SkeletonBlock } from '../components/ui'
import type { ChatMessage, ChatAgent } from '../store/api/chatApi'
import { useSSE } from '../hooks/useSSE'
import { useLoginPrompt } from '../context/LoginPromptContext'
import { useToast } from '../context/ToastContext'
import s from '../styles/pages/ChatPage.module.css'
import { AIMarkdownContent } from '../features/comments/AIMarkdownContent'
import { timeAgoShort } from '../lib/avatar'
import { api } from '../lib/apiClient'
import { ENDPOINTS } from '../lib/endpoints'
import { openRazorpayCheckout } from '../lib/razorpay'
import {
  COPY_FEEDBACK_MS, CHAT_RATE_LIMIT_PER_MIN, CHAT_RATE_WINDOW_MS,
  CHAT_RESPONSE_TIMEOUT_MS, CHAT_SEND_TIMEOUT_MS, CATEGORY_BADGE_PREVIEW_MAX,
  DROPDOWN_AUTOFOCUS_DELAY_MS, DOM_EVENTS,
} from '../lib/config'

const MESSAGES_PAGE = 20

const DEFAULT_VISUAL = { icon: '🤖', color: '#7c3aed', bg: 'rgba(124,58,237,0.12)' }
function getVisual(agent: { icon?: string | null; color?: string | null; name: string }) {
  const icon = agent.icon ?? DEFAULT_VISUAL.icon
  const color = agent.color ?? DEFAULT_VISUAL.color
  const bg = color + '1e'
  return { icon, color, bg }
}

const AGENT_SUGGESTIONS: Record<string, string[]> = {
  ChefBot:   ['How do I make perfect biryani?', 'Best way to make Dal Makhani', 'Tips for Indian street food at home'],
  WanderBot: ['Best hill stations near Bangalore', 'Budget trip to Rajasthan', 'Hidden gems in South India'],
  FitCoach:  ['Beginner gym workout plan', 'How to lose weight with Indian diet', 'Home workout without equipment'],
  TaxGuru:   ['How to file ITR for the first time', 'Save tax under 80C', 'GST basics for freelancers'],
  ZenMind:   ['How to manage work stress', 'Morning mindfulness routine', 'Tips for better sleep'],
  MelodyAI:  ['Best Carnatic ragas to learn', 'How to start playing guitar', 'Bollywood music theory'],
  UPSCAce:   ['UPSC preparation roadmap', 'Best books for GS Paper 1', 'How to write UPSC answers'],
  GeneralAI: ['Explain quantum computing simply', 'Best productivity tips', 'How does AI actually work?'],
}
const DEFAULT_SUGGESTIONS = ['Ask me anything', 'How can I help you?', 'What would you like to know?']

// ── Markdown renderer ──
function renderMarkdown(text: string, isStreaming = false): React.ReactNode {
  const lines = text.split('\n')
  const result: React.ReactNode[] = []
  lines.forEach((line, lineIdx) => {
    const formatInline = (str: string): React.ReactNode[] => {
      const parts = str.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g)
      return parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**') && part.length > 4)
          return <strong key={i} className={s.mdBold}>{part.slice(2, -2)}</strong>
        if (part.startsWith('`') && part.endsWith('`') && part.length > 2)
          return <code key={i} className={s.mdCode}>{part.slice(1, -1)}</code>
        if (part.startsWith('*') && part.endsWith('*') && part.length > 2)
          return <em key={i} className={s.mdItalic}>{part.slice(1, -1)}</em>
        return part.replace(/\*/g, '')
      })
    }
    const trimmed = line.trim()
    if (!trimmed) { if (lineIdx > 0) result.push(<div key={`sp-${lineIdx}`} className={s.mdSpacer} />); return }
    const numberedMatch = trimmed.match(/^(\d+)[.)\s]\s*(.+)$/)
    if (numberedMatch) { result.push(<div key={lineIdx} className={s.mdNumberedItem}><span className={s.mdNumberLabel}>{numberedMatch[1]}.</span><span>{formatInline(numberedMatch[2]!)}</span></div>); return }
    const bulletMatch = trimmed.match(/^[-•]\s+(.+)$/)
    if (bulletMatch) { result.push(<div key={lineIdx} className={s.mdListItem}><span className={s.mdBullet}>•</span><span>{formatInline(bulletMatch[1]!)}</span></div>); return }
    result.push(<span key={lineIdx}>{formatInline(trimmed)}<br /></span>)
  })
  // Add blinking cursor at end if streaming
  if (isStreaming) result.push(<span key="cursor" className={s.streamingCursor} />)
  return result
}

// ── Message bubble ──
function MessageBubble({ message, agentVisual, isLastAgent, isStreaming = false, showTime = true, onCopy, onPin, isPinned, conversationId, onUnlocked, freeUnlocksRemaining, onFreeUnlockUsed }: {
  message: ChatMessage
  agentVisual: { icon: string; color: string; bg: string }
  isLastAgent: boolean
  isStreaming?: boolean
  showTime?: boolean
  onCopy?: (text: string) => void
  onPin?: () => void
  isPinned?: boolean
  conversationId?: string
  onUnlocked?: (messageId: string, content: string) => void
  // Webhook (external) agents only — null for hosted agents, which have no
  // free tier and always require payment.
  freeUnlocksRemaining?: number | null
  onFreeUnlockUsed?: () => void
}) {
  const isUser = message.senderType === 'user'
  const isDeleted = message.isDeleted
  const isLocked = message.isPremium && !message.isDeleted && message.unlocked === false
  const isUnlockedPremium = message.isPremium && !isUser && !message.isDeleted && message.unlocked === true
  const isFreeUnlock = !!freeUnlocksRemaining && freeUnlocksRemaining > 0
  const [hovered, setHovered] = useState(false)
  const [copied, setCopied] = useState(false)
  const [unlocking, setUnlocking] = useState(false)
  const [downloadingInvoice, setDownloadingInvoice] = useState(false)
  const [invoiceOpened, setInvoiceOpened] = useState(false)

  const { accessToken } = useAppSelector(st => st.auth)
  const toast = useToast()
  const [createOrder] = useCreateMessageUnlockOrderMutation()
  const [unlockMsg] = useUnlockMessageMutation()
  const [unlockFree] = useUnlockMessageFreeMutation()

  const handleDownloadInvoice = async () => {
    if (!conversationId || downloadingInvoice) return
    // Opened synchronously, before the fetch — a tab opened after an await no
    // longer counts as "the user just clicked something" and gets silently
    // blocked in several browsers. Navigating an already-open blank tab once
    // the PDF arrives sidesteps that, and doubles as viewing the receipt.
    const win = window.open('', '_blank')
    setDownloadingInvoice(true); setInvoiceOpened(false)
    try {
      const { blob, filename } = await api.downloadFile('ai', ENDPOINTS.chat.unlockInvoice(conversationId, message.id), accessToken)
      const url = URL.createObjectURL(blob)
      if (win) {
        win.location.href = url
      } else {
        const a = document.createElement('a')
        a.href = url
        a.download = filename ?? 'invoice.pdf'
        document.body.appendChild(a)
        a.click()
        a.remove()
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
      setInvoiceOpened(true)
      setTimeout(() => setInvoiceOpened(false), 4000)
    } catch {
      win?.close()
      /* transient — the row exists, retry regenerates the PDF */
    } finally {
      setDownloadingInvoice(false)
    }
  }

  const handleUnlock = async () => {
    if (!conversationId || unlocking) return
    setUnlocking(true)
    try {
      if (isFreeUnlock) {
        const result = await unlockFree({ conversationId, messageId: message.id }).unwrap()
        onUnlocked?.(message.id, result.content)
        onFreeUnlockUsed?.()
        return
      }
      const orderRes = await createOrder({ conversationId, messageId: message.id }).unwrap()
      const payment = await openRazorpayCheckout({
        keyId:       orderRes.keyId,
        orderId:     orderRes.orderId,
        amount:      orderRes.amountPaise,
        currency:    orderRes.currency,
        name:        message.senderName ?? 'AI',
        description: 'Unlock premium message',
      })
      const result = await unlockMsg({
        conversationId,
        messageId:         message.id,
        razorpayOrderId:   payment.razorpayOrderId,
        razorpayPaymentId: payment.razorpayPaymentId,
        razorpaySignature: payment.razorpaySignature,
      }).unwrap()
      onUnlocked?.(message.id, result.content)
      toast.success(`Payment of ₹${(orderRes.amountPaise / 100).toLocaleString('en-IN')} received — ${message.senderName ?? 'agent'} reply unlocked. Your invoice is in Settings → Billing.`)
    } catch (err: any) {
      // A dismissed checkout is not an error; anything else (notably a failed
      // confirm *after* the user paid) must not be silent.
      if (err?.message !== 'cancelled') {
        toast.error(err?.data?.message ?? err?.message ?? 'Could not complete the payment. If you were charged, please contact support.')
      }
    } finally {
      setUnlocking(false)
    }
  }

  const handleCopy = () => {
    if (!message.content) return
    const plain = message.content.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/\*([^*]+)\*/g, '$1').replace(/`([^`]+)`/g, '$1')
    navigator.clipboard.writeText(plain).then(() => {
      setCopied(true); onCopy?.(plain); setTimeout(() => setCopied(false), COPY_FEEDBACK_MS)
    }).catch(() => {})
  }

  return (
    <div
      className={s.messageBubble}
      style={{ flexDirection: isUser ? 'row-reverse' : 'row', marginBottom: showTime ? '0.75rem' : '0.15rem' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false) }}
    >
      {/* Agent avatar */}
      {!isUser && (
        <div style={{
          width: 30, height: 30, borderRadius: '10px', flexShrink: 0,
          background: agentVisual.bg, border: `1px solid ${agentVisual.color}33`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px',
          alignSelf: 'flex-end', opacity: isLastAgent ? 1 : 0, transition: 'opacity 0.2s',
          boxShadow: isLastAgent ? `0 0 12px ${agentVisual.color}22` : 'none',
        }}>{agentVisual.icon}</div>
      )}

      <div className={s.bubbleGroup} style={{ flexDirection: isUser ? 'row-reverse' : 'row' }}>
        <div className={s.bubbleCol} style={{ alignItems: isUser ? 'flex-end' : 'flex-start' }}>
          {/* Bubble */}
          <div style={{
            padding: '0.65rem 1rem',
            borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
            background: isUser
              ? `linear-gradient(135deg, ${agentVisual.color}, ${agentVisual.color}cc)`
              : 'var(--bg-elevated)',
            border: isUser ? 'none' : message.isPremium ? '1.5px solid rgba(234,179,8,0.6)' : `1px solid var(--border)`,
            boxShadow: isUser ? `0 3px 16px ${agentVisual.color}44` : message.isPremium ? '0 0 0 1px rgba(234,179,8,0.15), 0 1px 6px rgba(0,0,0,0.25)' : '0 1px 6px rgba(0,0,0,0.25)',
            color: isUser ? 'white' : isDeleted ? 'var(--text-muted)' : 'var(--text-primary)',
            fontSize: '0.875rem',
            lineHeight: 1.65,
            wordBreak: 'break-word',
            fontStyle: isDeleted ? 'italic' : 'normal',
            maxWidth: '100%',
            position: 'relative',
            overflow: 'hidden',
          }}>
            {isDeleted ? 'Message deleted' : isLocked ? (
              <div style={{ position: 'relative', borderRadius: '6px', overflow: 'hidden', minHeight: '96px', maxHeight: '96px', margin: '-0.1rem' }}>
                <div style={{ filter: 'blur(6px)', opacity: 0.6, userSelect: 'none', pointerEvents: 'none', lineHeight: 1.65 }} aria-hidden="true">
                  <div>This response contains detailed guidance and step-by-step explanations tailored specifically for you.</div>
                  <div>It covers the core concepts, practical examples, and actionable insights that will help you move forward with confidence.</div>
                  <div>Unlock to read the full answer and get the most out of this conversation with the agent.</div>
                </div>
                <div style={{
                  position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: '0.6rem', padding: '0.75rem',
                  background: 'rgba(20,14,4,0.45)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)',
                }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'white', textAlign: 'center', textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>
                    🔒 Premium response
                  </span>
                  <button
                    onClick={handleUnlock}
                    disabled={unlocking}
                    style={{ padding: '0.4rem 0.9rem', background: 'rgba(234,179,8,0.9)', color: '#1c1100', border: 'none', borderRadius: '6px', fontWeight: 700, fontSize: '0.78rem', cursor: unlocking ? 'not-allowed' : 'pointer', opacity: unlocking ? 0.7 : 1, flexShrink: 0, boxShadow: '0 2px 10px rgba(234,179,8,0.35)' }}
                  >
                    {unlocking
                      ? 'Unlocking…'
                      : isFreeUnlock
                        ? `Unlock free (${freeUnlocksRemaining} left)`
                        : `Unlock for ₹${((message.premiumAmountPaise ?? 0) / 100).toFixed(0)}`}
                  </button>
                </div>
              </div>
            ) : isUser
              ? renderMarkdown(message.content ?? '', isStreaming)
              : <><AIMarkdownContent content={message.content ?? ''} />{isStreaming && <span className={s.streamingCursor} />}</>
            }
          </div>

          {/* Tax invoice for an unlocked premium reply */}
          {isUnlockedPremium && conversationId && (
            <div style={{ marginTop: '0.3rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                onClick={handleDownloadInvoice}
                disabled={downloadingInvoice}
                style={{
                  padding: 0, background: 'none', border: 'none',
                  color: 'var(--text-muted)', fontSize: '0.68rem', textDecoration: 'underline',
                  cursor: downloadingInvoice ? 'default' : 'pointer',
                }}
              >
                {downloadingInvoice ? 'Opening invoice…' : '👁 View / download tax invoice'}
              </button>
              {invoiceOpened && <span style={{ color: 'var(--success)', fontSize: '0.66rem' }}>✓ Opened in a new tab</span>}
            </div>
          )}

          {/* Timestamp */}
          {showTime && (
            <div style={{
              fontSize: '0.62rem', color: 'transparent', marginTop: '0.15rem',
              paddingLeft: isUser ? 0 : '0.25rem', paddingRight: isUser ? '0.25rem' : 0,
              transition: 'color 0.2s',
            }}
              onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.color = 'var(--text-muted)' }}
              onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.color = 'transparent' }}
            >{timeAgoShort(message.createdAt)}</div>
          )}
        </div>

        {/* Action buttons */}
        {!isDeleted && !isStreaming && (
          <div className={s.bubbleActions} style={{ opacity: hovered ? 1 : 0, pointerEvents: hovered ? 'auto' : 'none', flexDirection: 'column' }}>
            <button onClick={handleCopy} title="Copy" className={s.msgActionBtn} style={{ color: copied ? 'var(--success)' : undefined }}>
              {copied ? '✓' : '⎘'}
            </button>
            {onPin && (
              <button
                onClick={onPin}
                title={isPinned ? 'Unpin' : 'Pin'}
                className={s.msgActionBtn}
                style={{ color: isPinned ? 'var(--warning)' : undefined, background: isPinned ? 'rgba(234,179,8,0.12)' : undefined }}
              >📌</button>
            )}

          </div>
        )}
      </div>
    </div>
  )
}

// ── Typing indicator ──
function TypingIndicator({ agentVisual }: { agentVisual: { icon: string; color: string; bg: string } }) {
  return (
    <div className={s.typingIndicator}>
      <style>{`@keyframes typingPulse { 0%, 100% { opacity: 0.3; transform: scale(0.8); } 50% { opacity: 1; transform: scale(1.2); } }`}</style>
      <div className={s.typingAvatar} style={{ background: agentVisual.bg, border: `1px solid ${agentVisual.color}33` }}>{agentVisual.icon}</div>
      <div className={s.typingBubble}>
        {[0, 1, 2].map(i => (
          <div key={i} style={{
            width: 7, height: 7, borderRadius: '50%', background: agentVisual.color,
            opacity: 0.3, animation: `typingPulse 1.4s ease-in-out ${i * 0.22}s infinite`,
          }} />
        ))}
      </div>
    </div>
  )
}

// ── Welcome screen ──
function WelcomeScreen({ agentName, agentVisual, onPrompt }: {
  agentName: string
  agentVisual: { icon: string; color: string; bg: string }
  onPrompt: (text: string) => void
}) {
  const suggestions: Record<string, string[]> = {
    ChefBot:   ['How do I make perfect biryani?', 'Best way to make Dal Makhani', 'Tips for Indian street food at home'],
    WanderBot: ['Best hill stations near Bangalore', 'Budget trip to Rajasthan', 'Hidden gems in South India'],
    FitCoach:  ['Beginner gym workout plan', 'How to lose weight with Indian diet', 'Home workout without equipment'],
    TaxGuru:   ['How to file ITR for the first time', 'Save tax under 80C', 'GST basics for freelancers'],
    ZenMind:   ['How to manage work stress', 'Morning mindfulness routine', 'Tips for better sleep'],
    MelodyAI:  ['Best Carnatic ragas to learn', 'How to start playing guitar', 'Bollywood music theory'],
    UPSCAce:   ['UPSC preparation roadmap', 'Best books for GS Paper 1', 'How to write UPSC answers'],
    GeneralAI: ['Explain quantum computing simply', 'Best productivity tips', 'How does AI actually work?'],
  }
  const prompts = suggestions[agentName] ?? ['Ask me anything', 'How can I help you?', 'What would you like to know?']
  return (
    <div className={s.welcomeScreen}>
      {/* Agent avatar — large, glowing */}
      <div style={{
        width: 80, height: 80, borderRadius: '22px',
        background: agentVisual.bg,
        border: `2px solid ${agentVisual.color}55`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '2.2rem',
        boxShadow: `0 0 0 8px ${agentVisual.color}10, 0 12px 40px ${agentVisual.color}30`,
        animation: 'fadeIn 0.4s ease',
      }}>{agentVisual.icon}</div>

      <div className={s.welcomeText}>
        <div className={s.welcomeTitle}>Chat with {agentName}</div>
        <div className={s.welcomeSub}>Ask anything — private conversation, just for you</div>
      </div>

      <div className={s.suggestions}>
        {prompts.map((prompt) => (
          <button
            key={prompt}
            onClick={() => onPrompt(prompt)}
            className={s.suggestionBtn}
            onMouseEnter={e => {
              const el = e.currentTarget as HTMLButtonElement
              el.style.borderColor = `${agentVisual.color}66`
              el.style.color = 'var(--text-primary)'
              el.style.background = agentVisual.bg
            }}
            onMouseLeave={e => {
              const el = e.currentTarget as HTMLButtonElement
              el.style.borderColor = 'var(--border)'
              el.style.color = 'var(--text-secondary)'
              el.style.background = 'var(--bg-elevated)'
            }}
          >
            <span className={s.suggestionArrow} style={{ color: agentVisual.color }}>→</span>
            {prompt}
          </button>
        ))}
      </div>
    </div>
  )
}

// ── Date divider ──
function DateDivider({ label }: { label: string }) {
  return (
    <div className={s.dateDivider}>
      <div className={s.dateDividerLine} />
      <span className={s.dateDividerLabel}>{label}</span>
      <div className={s.dateDividerLine} />
    </div>
  )
}

function formatDateLabel(dateStr: string): string {
  const d = new Date(dateStr)
  const now = new Date()
  const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000)
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: diffDays > 300 ? 'numeric' : undefined })
}

// ── Main component ──
export default function ChatPage() {
  const { agentId: routeAgentId } = useParams<{ agentId?: string }>()
  const navigate = useNavigate()
  const { isAuthenticated, user, accessToken } = useAppSelector((s) => s.auth)
  const dispatch = useAppDispatch()
  const { trigger: promptLogin } = useLoginPrompt()

  const { data: conversationsData } = useGetConversationsQuery(undefined, { skip: !isAuthenticated })
  const conversationsByAgentId = useMemo(() => {
    const map: Record<string, { lastMessage: { content: string; senderType: string } | null }> = {}
    conversationsData?.conversations.forEach(c => { if (c.agentId) map[c.agentId] = { lastMessage: c.lastMessage } })
    return map
  }, [conversationsData])

  // ── Agent list state ──
  const [agentSearch, setAgentSearch] = useState('')
  const [agentSearchDebounced, setAgentSearchDebounced] = useState('')
  const [activeCategories, setActiveCategories] = useState<string[]>([])
  const agentDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const filterRef = useRef<HTMLDivElement>(null)
  const [filterOpen, setFilterOpen] = useState(false)
  const [filterSearch, setFilterSearch] = useState('')

  useEffect(() => {
    if (agentDebounceRef.current) clearTimeout(agentDebounceRef.current)
    agentDebounceRef.current = setTimeout(() => setAgentSearchDebounced(agentSearch), 300)
    return () => { if (agentDebounceRef.current) clearTimeout(agentDebounceRef.current) }
  }, [agentSearch])

  const AGENTS_PAGE = 20
  const [agentOffset, setAgentOffset] = useState(0)
  const [allAgents, setAllAgents] = useState<ChatAgent[]>([])
  const [agentMap, setAgentMap] = useState<Map<string, ChatAgent>>(new Map())
  const [loadingMoreAgents, setLoadingMoreAgents] = useState(false)

  const { data: agentsData, isLoading: agentsLoading } = useGetChatAgentsQuery(
    { q: agentSearchDebounced || undefined, categories: activeCategories.length > 0 ? activeCategories : undefined, limit: AGENTS_PAGE, offset: agentOffset }
  )
  const agents = agentsData?.agents ?? []

  useEffect(() => { setAgentOffset(0); setAllAgents([]) }, [agentSearchDebounced, activeCategories])
  useEffect(() => {
    if (!agentsData) return
    if (agentOffset === 0) {
      setAllAgents(agentsData.agents)
    } else {
      setAllAgents(prev => {
        const existingIds = new Set(prev.map(a => a.id))
        const newOnes = agentsData.agents.filter(a => !existingIds.has(a.id))
        return newOnes.length > 0 ? [...prev, ...newOnes] : prev
      })
    }
    setAgentMap(prev => {
      const next = new Map(prev)
      agentsData.agents.forEach(a => next.set(a.id, a))
      return next
    })
    setLoadingMoreAgents(false)
  }, [agentsData, agentOffset])

  const agentsHasMore = agentsData?.hasMore ?? false
  const filteredAgents = useMemo(() => {
    // Inject stubs for conversation-agents not yet in the paginated allAgents list.
    // Skip when a category filter is active — stubs have no category data and would
    // show up incorrectly even though they don't match the selected category.
    const loadedIds = new Set(allAgents.map(a => a.id))
    const stubAgents: ChatAgent[] = []
    if (activeCategories.length === 0) {
      conversationsData?.conversations.forEach(conv => {
        if (conv.agentId && !loadedIds.has(conv.agentId)) {
          stubAgents.push({
            id: conv.agentId, name: conv.agentName ?? 'AI',
            description: '', categoryIds: [], categories: [],
            icon: null, color: null, status: 'active',
          })
        }
      })
    }

    return [...stubAgents, ...allAgents].sort((a, b) => {
      const aConv = conversationsData?.conversations.find(c => c.agentId === a.id)
      const bConv = conversationsData?.conversations.find(c => c.agentId === b.id)
      const aTime = aConv?.lastMessage?.createdAt
      const bTime = bConv?.lastMessage?.createdAt
      if (aTime && bTime) return new Date(bTime).getTime() - new Date(aTime).getTime()
      if (aTime) return -1
      if (bTime) return 1
      if (aConv && bConv) return new Date(bConv.updatedAt).getTime() - new Date(aConv.updatedAt).getTime()
      if (aConv) return -1; if (bConv) return 1; return 0
    })
  }, [allAgents, conversationsData])

  const loadMoreAgents = () => {
    if (loadingMoreAgents || !agentsHasMore) return
    setLoadingMoreAgents(true); setAgentOffset(prev => prev + AGENTS_PAGE)
  }

  const { data: categoriesData } = useGetChatCategoriesQuery()
  const agentCategories = categoriesData?.categories ?? []

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) { setFilterOpen(false); setFilterSearch('') }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // ── Chat state ──
  const [activeAgentId, setActiveAgentId] = useState<string | null>(routeAgentId ?? null)
  const [mobileChatOpen, setMobileChatOpen] = useState<boolean>(!!routeAgentId)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [streamingMessage, setStreamingMessage] = useState<{ streamId: string; content: string } | null>(null)
  const [isTyping, setIsTyping] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [chatCapInfo, setChatCapInfo] = useState<{ resetAt: string } | null>(null)

  const { data: aiUsageData } = useGetAiUsageQuery(undefined, { skip: !isAuthenticated, pollingInterval: 60000 })
  const chatCapReached = isAuthenticated && (!!chatCapInfo || (aiUsageData?.chat.remaining === 0))
  const chatCapResetTime = chatCapInfo?.resetAt
    ?? (aiUsageData?.chat.resetAt
      ? new Date(aiUsageData.chat.resetAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })
      : null)

  const { data: capConfigData } = useGetSpendingCapConfigQuery()
  const capConfig = capConfigData?.config

  // Interpolate {limit}, {used}, {resetTime} placeholders
  const interp = (tmpl: string, vars: Record<string, string | number>) =>
    tmpl.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? `{${k}}`))

  // ── Search ──
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchAllResults, setSearchAllResults] = useState<ChatMessage[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const runSearch = useCallback(async (query: string, convId: string) => {
    if (!query.trim()) { setSearchAllResults([]); return }
    setSearchLoading(true)
    try {
      const d = await api.get<{ data: ChatMessage[] }>('ai', `${ENDPOINTS.chat.searchMessages(convId)}?q=${encodeURIComponent(query)}`, accessToken)
      setSearchAllResults(d.data)
    } catch {}
    setSearchLoading(false)
  }, [accessToken])

  useEffect(() => {
    if (!searchOpen || !conversationId) return
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    searchDebounceRef.current = setTimeout(() => { void runSearch(searchQuery, conversationId) }, 300)
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current) }
  }, [searchQuery, searchOpen, conversationId])

  const searchResults = searchQuery.trim()
    ? searchAllResults.length > 0
      ? searchAllResults
      : messages.filter(m => !m.isDeleted && m.content?.toLowerCase().includes(searchQuery.toLowerCase()))
    : []
  const [searchIdx, setSearchIdx] = useState(0)
  const messageRefs = useRef<Record<string, HTMLDivElement | null>>({})

  const openSearch = () => { setSearchOpen(true); setTimeout(() => searchInputRef.current?.focus(), DROPDOWN_AUTOFOCUS_DELAY_MS) }
  const closeSearch = () => { setSearchOpen(false); setSearchQuery(''); setSearchIdx(0); setSearchAllResults([]) }

  // ── Pagination ──
  const nextCursorRef = useRef<string | null>(null)
  const messagesAreaRef = useRef<HTMLDivElement>(null)
  const conversationIdRef = useRef<string | null>(null)
  const navigatingRef = useRef(false)

  const navigateToMessage = useCallback(async (msgId: string) => {
    const el = messageRefs.current[msgId]
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); return }
    if (navigatingRef.current) return
    navigatingRef.current = true
    let cursor = nextCursorRef.current
    let found = false
    while (cursor && !found) {
      try {
        const convId = conversationIdRef.current
        if (!convId) break
        const d = await api.get<{ data: ChatMessage[]; nextCursor: string | null; hasMore: boolean }>(
          'ai', `${ENDPOINTS.chat.messages(convId)}?limit=${MESSAGES_PAGE}&cursor=${encodeURIComponent(cursor)}`, accessToken
        )
        const area = messagesAreaRef.current
        const prevScrollHeight = area?.scrollHeight ?? 0
        setMessages(prev => { const existingIds = new Set(prev.map(m => m.id)); const newMsgs = d.data.filter(m => !existingIds.has(m.id)); return newMsgs.length > 0 ? [...newMsgs, ...prev] : prev })
        setHasMore(d.hasMore); nextCursorRef.current = d.nextCursor; setNextCursor(d.nextCursor)
        requestAnimationFrame(() => { if (area) area.scrollTop = area.scrollHeight - prevScrollHeight })
        cursor = d.nextCursor; found = d.data.some(m => m.id === msgId)
      } catch { break }
    }
    navigatingRef.current = false
    setTimeout(() => { const el2 = messageRefs.current[msgId]; if (el2) el2.scrollIntoView({ behavior: 'smooth', block: 'center' }) }, 150)
  }, [accessToken])

  useEffect(() => { conversationIdRef.current = conversationId }, [conversationId])

  const searchResultsRef = useRef<ChatMessage[]>([])
  const searchIdxRef = useRef(0)
  searchResultsRef.current = searchResults
  searchIdxRef.current = searchIdx

  const goSearchPrev = useCallback(() => {
    if (searchResultsRef.current.length === 0) return
    const idx = (searchIdxRef.current - 1 + searchResultsRef.current.length) % searchResultsRef.current.length
    setSearchIdx(idx); void navigateToMessage(searchResultsRef.current[idx]!.id)
  }, [navigateToMessage])

  const goSearchNext = useCallback(() => {
    if (searchResultsRef.current.length === 0) return
    const idx = (searchIdxRef.current + 1) % searchResultsRef.current.length
    setSearchIdx(idx); void navigateToMessage(searchResultsRef.current[idx]!.id)
  }, [navigateToMessage])

  const prevSearchQueryRef = useRef('')
  useEffect(() => {
    if (searchResults.length > 0 && searchQuery !== prevSearchQueryRef.current) {
      prevSearchQueryRef.current = searchQuery; setSearchIdx(0); void navigateToMessage(searchResults[0]!.id)
    }
  }, [searchResults, searchQuery, navigateToMessage])

  // ── Pinned messages ──
  const [pinnedIds, setPinnedIds] = useState<string[]>([])
  const [showPinned, setShowPinned] = useState(false)
  const [pinnedMessageData, setPinnedMessageData] = useState<Record<string, ChatMessage>>({})

  useEffect(() => {
    if (pinnedIds.length === 0) return
    const updates: Record<string, ChatMessage> = {}
    messages.forEach(m => { if (pinnedIds.includes(m.id)) updates[m.id] = m })
    if (Object.keys(updates).length > 0) setPinnedMessageData(prev => ({ ...prev, ...updates }))
  }, [messages, pinnedIds])

  useEffect(() => {
    if (!conversationId || pinnedIds.length === 0) return
    const missing = pinnedIds.filter(id => !pinnedMessageData[id])
    if (missing.length === 0) return
    void (async () => {
      try {
        const d = await api.get<{ data: ChatMessage[] }>('ai', `${ENDPOINTS.chat.messagesByIds(conversationId)}?ids=${missing.join(',')}`, accessToken)
        const byId: Record<string, ChatMessage> = {}; d.data.forEach(m => { byId[m.id] = m })
        if (Object.keys(byId).length > 0) setPinnedMessageData(prev => ({ ...prev, ...byId }))
      } catch {}
    })()
  }, [pinnedIds, conversationId, accessToken])

  const pinnedMessages = useMemo(() =>
    pinnedIds.map(id => pinnedMessageData[id]).filter(Boolean) as ChatMessage[],
    [pinnedIds, pinnedMessageData]
  )

  useEffect(() => {
    if (!conversationId) return
    try { const stored = localStorage.getItem(`pins:${conversationId}`); setPinnedIds(stored ? JSON.parse(stored) : []); setPinnedMessageData({}) } catch { setPinnedIds([]) }
  }, [conversationId])

  const togglePin = useCallback((msgId: string) => {
    const msgData = messages.find(m => m.id === msgId)
    if (msgData) setPinnedMessageData(prev => ({ ...prev, [msgId]: msgData }))
    setPinnedIds(prev => {
      const next = prev.includes(msgId) ? prev.filter(id => id !== msgId) : [...prev, msgId]
      if (conversationId) { try { localStorage.setItem(`pins:${conversationId}`, JSON.stringify(next)) } catch {} }
      return next
    })
  }, [conversationId, messages])

  // ── Typing / streaming ──
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const stopTyping = useCallback((err?: string) => {
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
    setIsTyping(false); setStreamingMessage(null)
    if (err) { setErrorMsg(err); setTimeout(() => setErrorMsg(null), 4000) }
  }, [])

  // ── Input ──
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [loadingConv, setLoadingConv] = useState(false)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  // Free trial unlocks left for the active conversation's agent (webhook
  // agents only — null for hosted agents / no premium messages).
  const [freeUnlocksRemaining, setFreeUnlocksRemaining] = useState<number | null>(null)
  const syncNextCursor = (v: string | null) => { nextCursorRef.current = v; setNextCursor(v) }
  const [lastMessages, setLastMessages] = useState<Record<string, { content: string; senderType: string }>>({})
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const topSentinelRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const inputContainerRef = useRef<HTMLDivElement>(null)

  // ── Scroll-to-bottom button ──
  const [showScrollBtn, setShowScrollBtn] = useState(false)
  useEffect(() => {
    const area = messagesAreaRef.current
    if (!area) return
    const handler = () => { setShowScrollBtn(area.scrollHeight - area.scrollTop - area.clientHeight > 200) }
    area.addEventListener('scroll', handler, { passive: true })
    return () => area.removeEventListener('scroll', handler)
  }, [])

  const [getOrCreateConversation] = useGetOrCreateConversationMutation()
  const [sendMessageMutation] = useSendMessageMutation()

  const fetchMessages = useCallback(async (convId: string) => {
    setLoadingMessages(true)
    try {
      const d = await api.get<{ data: ChatMessage[]; nextCursor: string | null; hasMore: boolean; freeUnlocksRemaining: number | null }>(
        'ai', `${ENDPOINTS.chat.messages(convId)}?limit=${MESSAGES_PAGE}`, accessToken
      )
      setMessages(d.data); setHasMore(d.hasMore); syncNextCursor(d.nextCursor)
      setFreeUnlocksRemaining(d.freeUnlocksRemaining)
      if (d.data.length > 0) {
        const last = d.data[d.data.length - 1]!
        setLastMessages(prev => ({ ...prev, [convId]: { content: last.content ?? '', senderType: last.senderType } }))
      }
    } catch {
      setErrorMsg('Failed to load messages. Please refresh.')
      setTimeout(() => setErrorMsg(null), 5000)
    }
    setLoadingMessages(false)
  }, [accessToken])

  const fetchOlderMessages = useCallback(async () => {
    if (!conversationId || !hasMore || !nextCursor || loadingMore) return
    const area = messagesAreaRef.current
    const prevScrollHeight = area?.scrollHeight ?? 0
    setLoadingMore(true)
    try {
      const d = await api.get<{ data: ChatMessage[]; nextCursor: string | null; hasMore: boolean }>(
        'ai', `${ENDPOINTS.chat.messages(conversationId)}?limit=${MESSAGES_PAGE}&cursor=${encodeURIComponent(nextCursor)}`, accessToken
      )
      setMessages(prev => [...d.data, ...prev]); setHasMore(d.hasMore); syncNextCursor(d.nextCursor)
      requestAnimationFrame(() => { if (area) area.scrollTop = area.scrollHeight - prevScrollHeight })
    } catch {
      setErrorMsg('Failed to load older messages.')
      setTimeout(() => setErrorMsg(null), 4000)
    }
    setLoadingMore(false)
  }, [conversationId, hasMore, nextCursor, loadingMore, accessToken])

  const resolvedActiveAgent = (activeAgentId ? agentMap.get(activeAgentId) : null)
    ?? allAgents.find(a => a.id === activeAgentId)
    ?? agents.find(a => a.id === activeAgentId)
    ?? null

  // If agent isn't in the active list (e.g. deactivated), fetch it directly
  const { data: fallbackAgentData } = useGetAgentByIdQuery(
    activeAgentId ?? '',
    { skip: !activeAgentId || !!resolvedActiveAgent }
  )

  const activeAgent = resolvedActiveAgent ?? fallbackAgentData?.agent ?? null
  const activeVisual = activeAgent ? getVisual(activeAgent) : DEFAULT_VISUAL
  const agentInactive = !!activeAgent && activeAgent.status !== 'active'

  useEffect(() => { if (!isAuthenticated) promptLogin('Sign in to chat with AI agents') }, [isAuthenticated])
  useEffect(() => { if (routeAgentId && routeAgentId !== activeAgentId) setActiveAgentId(routeAgentId) }, [routeAgentId])

  // Auto-select first agent if none is active and no route param
  useEffect(() => {
    if (!activeAgentId && !routeAgentId && filteredAgents.length > 0) {
      const first = filteredAgents[0]!
      setActiveAgentId(first.id)
      void navigate(`/chat/${first.id}`, { replace: true })
    }
  }, [filteredAgents, activeAgentId, routeAgentId])

  const loadedForRef = useRef<string | null>(null)
  useEffect(() => {
    if (!activeAgentId || !isAuthenticated || !accessToken) return
    if (loadedForRef.current === activeAgentId) return
    loadedForRef.current = activeAgentId
    setLoadingConv(true); setMessages([]); setStreamingMessage(null); setIsTyping(false); setErrorMsg(null); setChatCapInfo(null); setFreeUnlocksRemaining(null)
    setSearchOpen(false); setSearchQuery(''); setSearchAllResults([]); setPinnedIds([]); setPinnedMessageData({})
    setShowPinned(false)
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
    setConversationId(null); setHasMore(false); syncNextCursor(null)
    getOrCreateConversation({ agentId: activeAgentId }).unwrap()
      .then(res => { setConversationId(res.conversation.id); void fetchMessages(res.conversation.id) })
      .catch(() => {
        loadedForRef.current = null
        setErrorMsg('Failed to connect to agent. Please try again.')
        setTimeout(() => setErrorMsg(null), 5000)
      })
      .finally(() => setLoadingConv(false))
  }, [activeAgentId, isAuthenticated, accessToken])

  // Auto-scroll on new messages
  const prevMessageCountRef = useRef(0)
  useEffect(() => {
    const count = messages.length
    if (count > prevMessageCountRef.current) {
      const area = messagesAreaRef.current
      if (area) {
        const isNearBottom = area.scrollHeight - area.scrollTop - area.clientHeight < 120
        if (isNearBottom || prevMessageCountRef.current === 0)
          messagesEndRef.current?.scrollIntoView({ behavior: prevMessageCountRef.current === 0 ? 'instant' : 'smooth' })
      }
    }
    prevMessageCountRef.current = count
  }, [messages])

  useEffect(() => {
    if (isTyping || streamingMessage) {
      const area = messagesAreaRef.current
      if (area) {
        const isNearBottom = area.scrollHeight - area.scrollTop - area.clientHeight < 200
        if (isNearBottom) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }, [isTyping, streamingMessage])

  useEffect(() => {
    if (!topSentinelRef.current || !hasMore) return
    const obs = new IntersectionObserver(
      entries => { if (entries[0]?.isIntersecting) void fetchOlderMessages() },
      { root: messagesAreaRef.current, threshold: 0.1 }
    )
    obs.observe(topSentinelRef.current)
    return () => obs.disconnect()
  }, [hasMore, fetchOlderMessages])

  // ── SSE ──
  useSSE({
    enabled: isAuthenticated && !!conversationId,
    onEvent: {
      dm_thinking: (data: unknown) => {
        const d = data as { conversationId: string }
        if (d.conversationId !== conversationId) return
        setIsTyping(true)
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
        typingTimeoutRef.current = setTimeout(() => stopTyping('No response received. Please try again.'), CHAT_RESPONSE_TIMEOUT_MS)
      },
      dm_token: (data: unknown) => {
        const d = data as { conversationId: string; streamId: string; token: string; done: boolean }
        if (d.conversationId !== conversationId) return
        if (d.done || !d.token) return
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
        setIsTyping(false)
        setStreamingMessage(prev => {
          if (!prev || prev.streamId !== d.streamId) return { streamId: d.streamId, content: d.token }
          return { streamId: d.streamId, content: prev.content + d.token }
        })
      },
      dm_message: (data: unknown) => {
        const d = data as { conversationId: string; messageId: string; agentId: string; agentName: string; content: string | null; isPremium?: boolean; premiumAmountPaise?: number | null; createdAt: string }
        if (d.conversationId !== conversationId) return
        stopTyping()
        setMessages(prev => {
          if (prev.some(m => m.id === d.messageId)) return prev
          return [...prev, {
            id: d.messageId, senderType: 'agent', senderId: d.agentId, senderName: d.agentName,
            content: d.content, isDeleted: false, editedAt: null, createdAt: d.createdAt,
            isPremium: d.isPremium ?? false, premiumAmountPaise: d.premiumAmountPaise ?? null,
            unlocked: d.isPremium ? false : null,
          }]
        })
        setLastMessages(prev => ({ ...prev, [d.conversationId]: { content: d.isPremium ? '🔒 Premium message' : (d.content ?? ''), senderType: 'agent' } }))
        dispatch(chatApi.util.invalidateTags(['ChatConversation']))
      },
      dm_error: (data: unknown) => {
        const d = data as { conversationId: string; message?: string }
        if (d.conversationId !== conversationId) return
        stopTyping(d.message ?? 'Something went wrong. Please try again.')
      },
    },
  })

  const handleSelectAgent = (agentId: string) => {
    loadedForRef.current = null; stopTyping()
    setActiveAgentId(agentId); setMobileChatOpen(true)
    void navigate(`/chat/${agentId}`, { replace: true })
  }

  const [clearing, setClearing] = useState(false)
  const handleClearChat = async () => {
    if (!conversationId || clearing) return
    if (!window.confirm('Clear all messages in this conversation?')) return
    setClearing(true)
    try {
      await api.delete('ai', ENDPOINTS.chat.clearMessages(conversationId), accessToken)
      setMessages([]); setStreamingMessage(null); stopTyping()
    } catch {
      setErrorMsg('Failed to clear chat. Please try again.')
      setTimeout(() => setErrorMsg(null), 4000)
    } finally { setClearing(false) }
  }

  // ── Client-side rate limit tracker ──
  // Note: this is separate from the daily AI spending cap (tracked via ai_requests table)
  // The chat rate limit is per-minute Redis-based, tracked client-side for UX only.
  // CHAT_RATE_LIMIT_PER_MIN must match the server-enforced limit or this display drifts
  // from what's actually enforced — see TopNav.tsx, which shows the same constant.
  const CHAT_RATE_LIMIT = CHAT_RATE_LIMIT_PER_MIN
  const [msgCountThisMinute, setMsgCountThisMinute] = useState(0)
  const msgWindowRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Broadcast initial state when chat page mounts so TopNav shows the indicator
  useEffect(() => {
    window.dispatchEvent(new CustomEvent(DOM_EVENTS.chatRate, { detail: { count: 0, limit: CHAT_RATE_LIMIT } }))
    return () => {
      if (msgWindowRef.current) clearTimeout(msgWindowRef.current)
      window.dispatchEvent(new CustomEvent(DOM_EVENTS.chatRate, { detail: { count: -1, limit: CHAT_RATE_LIMIT } }))
    }
  }, [])

  const trackMsgSent = useCallback(() => {
    setMsgCountThisMinute(prev => {
      const next = prev + 1
      // Broadcast to TopNav via custom event
      window.dispatchEvent(new CustomEvent(DOM_EVENTS.chatRate, { detail: { count: next, limit: CHAT_RATE_LIMIT } }))
      return next
    })
    if (!msgWindowRef.current) {
      msgWindowRef.current = setTimeout(() => {
        setMsgCountThisMinute(0)
        window.dispatchEvent(new CustomEvent(DOM_EVENTS.chatRate, { detail: { count: 0, limit: CHAT_RATE_LIMIT } }))
        msgWindowRef.current = null
      }, CHAT_RATE_WINDOW_MS)
    }
  }, [])

  const handleSend = useCallback(async (overrideContent?: string) => {
    const content = (overrideContent ?? input).trim()
    if (!content || sending || !conversationId) return
    if (!isAuthenticated) { promptLogin('Sign in to chat'); return }
    setInput(''); setSending(true); setIsTyping(true)
    if (textareaRef.current) textareaRef.current.style.height = 'auto'
    const tempId = `temp-${Date.now()}`
    setMessages(prev => [...prev, {
      id: tempId, senderType: 'user', senderId: user?.id ?? '',
      senderName: null, content, isDeleted: false, editedAt: null,
      createdAt: new Date().toISOString(),
      isPremium: false, premiumAmountPaise: null, unlocked: null,
    }])
    const timeoutId = setTimeout(() => {
      setIsTyping(false); setSending(false)
      setMessages(prev => prev.filter(m => m.id !== tempId)); setInput(content)
      setErrorMsg('Message timed out. Check your connection and try again.')
      setTimeout(() => setErrorMsg(null), 5000)
      if (textareaRef.current) { textareaRef.current.focus(); textareaRef.current.style.height = 'auto'; textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px` }
    }, CHAT_SEND_TIMEOUT_MS)
    try {
      const res = await sendMessageMutation({ conversationId, content }).unwrap()
      clearTimeout(timeoutId)
      trackMsgSent()
      setMessages(prev => prev.map(m => m.id === tempId ? { ...res.message, senderName: null } : m))
      setLastMessages(prev => ({ ...prev, [conversationId]: { content, senderType: 'user' } }))
    } catch (err: any) {
      clearTimeout(timeoutId); setIsTyping(false)
      setMessages(prev => prev.filter(m => m.id !== tempId)); setInput(content)
      console.error('[ChatPage] send error:', JSON.stringify(err))
      const status = err?.status ?? err?.originalStatus
      const body = err?.data ?? err?.body ?? {}
      const isSpendingCap = status === 429 && body?.code === 'SPENDING_CAP_REACHED'
      if (isSpendingCap) {
        const resetTime = body?.resetAt
          ? new Date(body.resetAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })
          : null
        setChatCapInfo({ resetAt: resetTime ?? 'midnight' })
      } else {
        setErrorMsg(status === 429
          ? 'Sending too fast. Wait a moment and try again.'
          : 'Message failed to send. Check your connection and try again.')
        setTimeout(() => setErrorMsg(null), 5000)
      }
      if (textareaRef.current) { textareaRef.current.focus(); textareaRef.current.style.height = 'auto'; textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px` }
    } finally { setSending(false) }
  }, [input, sending, conversationId, isAuthenticated, user?.id, sendMessageMutation])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void handleSend() }
  }

  if (!isAuthenticated) return null
  const isEmpty = !loadingMessages && !loadingConv && messages.length === 0 && !isTyping

  // ── Date grouping ──
  const messagesWithDividers = useMemo(() => {
    const result: Array<{ type: 'message'; message: ChatMessage; showAvatar: boolean; showTime: boolean } | { type: 'date'; label: string }> = []
    let lastDateLabel = ''
    messages.forEach((msg, idx) => {
      const dateLabel = formatDateLabel(msg.createdAt)
      if (dateLabel !== lastDateLabel) {
        result.push({ type: 'date', label: dateLabel })
        lastDateLabel = dateLabel
      }
      const next = messages[idx + 1]
      const isLastInGroup = !next || next.senderType !== msg.senderType
      result.push({ type: 'message', message: msg, showAvatar: isLastInGroup, showTime: isLastInGroup })
    })
    return result
  }, [messages])

  return (
    <div className={s.page} data-chat-open={mobileChatOpen ? 'true' : 'false'}>
      {/* ── Left sidebar ── */}
      <div className="sidebar-left" style={{ width: '374px' }}>
        <aside style={{ width: '100%', paddingTop: '1rem', paddingBottom: '1rem', paddingRight: '0.75rem', display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
        <div className={s.sidebarHeader}>
          <div className={s.sidebarHeaderBlock}>
            <div className={s.sidebarHeaderRow}>
              <div className={s.sidebarIconBox}>💬</div>
              <div className={s.sidebarTitleWrap}>
                <div className={s.sidebarTitle}>AI Chat</div>
                <div className={s.sidebarSubtitle}>Private conversations</div>
              </div>
              {!agentsLoading && (
                <MultiSelectDropdown
                  options={agentCategories.map(c => ({ value: c.slug, label: c.name, icon: c.icon, color: c.color }))}
                  selected={activeCategories}
                  onChange={setActiveCategories}
                  placeholder="Filter"
                  searchPlaceholder="Search categories..."
                />
              )}
            </div>
            <SearchInput
              value={agentSearch}
              onChange={e => setAgentSearch(e.target.value)}
              placeholder="Search agents..."
              className={s.searchInputFontSize}
              containerStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '8px' }}
            />
            {activeCategories.length > 0 && (
              <div className={s.categoryChips}>
                {activeCategories.map(slug => {
                  const cat = agentCategories.find(c => c.slug === slug)
                  if (!cat) return null
                  return (
                    <span key={slug} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.15rem 0.45rem', borderRadius: '20px', fontSize: '0.68rem', background: `${cat.color}20`, border: `1px solid ${cat.color}55`, color: cat.color, fontWeight: 500 }}>
                      <span className={s.catChipIcon}>{cat.icon}</span>{cat.name}
                      <button onClick={() => setActiveCategories(prev => prev.filter(s => s !== slug))} className={s.catChipRemove} style={{ color: cat.color, opacity: 0.7, marginLeft: '0.1rem' }}>✕</button>
                    </span>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        <div className={s.agentList}>
          {agentsLoading
            ? [1,2,3,4,5].map(i => (
                <div key={i} className={s.agentSkeletonRow}>
                  <SkeletonBlock height="36px" width="36px" borderRadius="10px" style={{ flexShrink: 0 }} />
                  <div className={s.agentSkeletonBody}>
                    <SkeletonBlock height="9px" width="65%" style={{ marginBottom: '5px' }} />
                    <SkeletonBlock height="7px" width="85%" delay={0.05} />
                  </div>
                </div>
              ))
            : filteredAgents.length === 0
              ? <div className={s.noAgents}>No agents found</div>
              : filteredAgents.map(agent => {
                  const v = getVisual(agent)
                  const isActive = agent.id === activeAgentId
                  const convPreview = (() => {
                    if (isActive && conversationId && lastMessages[conversationId]) return lastMessages[conversationId]
                    const conv = conversationsByAgentId[agent.id]
                    if (conv?.lastMessage) return { content: conv.lastMessage.content, senderType: conv.lastMessage.senderType }
                    return null
                  })()
                  const previewText = convPreview
                    ? (convPreview.senderType === 'user' ? 'You: ' : '') + convPreview.content.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/\*([^*]+)\*/g, '$1').slice(0, 35) + (convPreview.content.length > 35 ? '…' : '')
                    : agent.description || 'AI Agent'
                  return (
                    <button
                      key={agent.id}
                      onClick={() => handleSelectAgent(agent.id)}
                      style={{
                        width: '100%', textAlign: 'left', padding: '0.6rem 0.6rem',
                        borderRadius: '10px', border: 'none',
                        background: isActive ? v.bg : 'transparent',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.6rem',
                        marginBottom: '0.1rem', transition: 'all 0.15s',
                        outline: isActive ? `1px solid ${v.color}44` : 'none',
                      }}
                      onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-elevated)' }}
                      onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = 'transparent' }}
                    >
                      <div style={{
                        width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
                        background: v.bg, border: `1px solid ${v.color}33`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '16px', boxShadow: isActive ? `0 0 12px ${v.color}44` : 'none',
                      }}>{v.icon}</div>
                      <div className={s.agentInfo}>
                        <div style={{ fontSize: '0.825rem', fontWeight: isActive ? 700 : 500, color: isActive ? v.color : 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{agent.name}</div>
                        <div style={{ fontSize: '0.68rem', color: isActive ? `${v.color}99` : 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: '0.1rem' }}>{previewText}</div>
                      </div>
                    </button>
                  )
                })
          }
          {agentsHasMore && (
            <button
              onClick={loadMoreAgents}
              disabled={loadingMoreAgents}
              className={s.loadMoreBtn}
            >
              {loadingMoreAgents ? <><div className={s.miniSpinner} /> Loading…</> : `Load more (${(agentsData?.total ?? 0) - filteredAgents.length} more)`}
            </button>
          )}
        </div>
        </aside>
      </div>

      {/* ── Center: chat area ── */}
      <div className="main-content" style={{ paddingRight: 0, overflow: 'hidden', maxWidth: '968px' }}>
        <div className={s.chatArea}>
        {!activeAgentId ? (
          <div className={s.noChatSelected}>
            <div className={s.noChatIcon}>🤖</div>
            <div className={s.noChatText}>
              <div className={s.noChatTitle}>Select an agent</div>
              <div className={s.noChatSub}>Choose an AI expert from the left to start a private conversation</div>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className={s.chatHeader}>
              <button
                className={s.chatHeaderBackBtn}
                onClick={() => setMobileChatOpen(false)}
                style={{ display: 'none', background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1.2rem', padding: '4px 8px 4px 0', lineHeight: 1, flexShrink: 0 }}
              >←</button>
              <div style={{
                width: 40, height: 40, borderRadius: '12px',
                background: activeVisual.bg, border: `1px solid ${activeVisual.color}44`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '20px', flexShrink: 0,
                boxShadow: `0 0 0 4px ${activeVisual.color}12, 0 4px 16px ${activeVisual.color}33`,
              }}>{activeVisual.icon}</div>
              <div className={s.chatHeaderInfo}>
                <div className={s.chatHeaderName}>{activeAgent?.name}</div>
                <div className={s.onlineRow}>
                  <div className={s.onlineDot} />
                  <span className={s.onlineLabel}>Online · Private conversation</span>
                </div>
              </div>

              {/* Desktop actions */}
              <div className={s.chatHeaderDesktopActions}>
                {messages.length > 0 && (
                  <button
                    onClick={() => void handleClearChat()}
                    disabled={clearing || isTyping}
                    title="Clear conversation"
                    className={s.headerBtn}
                    onMouseEnter={e => { const el = e.currentTarget as HTMLButtonElement; el.style.borderColor = 'rgba(239,68,68,0.4)'; el.style.color = 'var(--error)' }}
                    onMouseLeave={e => { const el = e.currentTarget as HTMLButtonElement; el.style.borderColor = 'var(--border)'; el.style.color = 'var(--text-muted)' }}
                  >🗑</button>
                )}
                {messages.length > 0 && (
                  <button
                    onClick={openSearch}
                    title="Search messages"
                    style={{
                      background: searchOpen ? `${activeVisual.color}20` : 'transparent',
                      border: `1px solid ${searchOpen ? activeVisual.color + '66' : 'var(--border)'}`,
                      borderRadius: '8px', padding: '0.35rem 0.6rem',
                      color: searchOpen ? activeVisual.color : 'var(--text-muted)',
                      cursor: 'pointer', fontSize: '0.72rem', transition: 'all 0.15s', flexShrink: 0,
                    }}
                  >🔍</button>
                )}
                {pinnedIds.length > 0 && (
                  <button
                    onClick={() => setShowPinned(p => !p)}
                    title="Pinned messages"
                    style={{
                      background: showPinned ? `${activeVisual.color}20` : 'transparent',
                      border: `1px solid ${showPinned ? activeVisual.color + '66' : 'var(--border)'}`,
                      borderRadius: '8px', padding: '0.35rem 0.6rem',
                      color: showPinned ? activeVisual.color : 'var(--text-muted)',
                      cursor: 'pointer', fontSize: '0.72rem', transition: 'all 0.15s',
                      flexShrink: 0, display: 'flex', alignItems: 'center', gap: '0.3rem',
                    }}
                  >📌 <span style={{ fontWeight: 600 }}>{pinnedIds.length}</span></button>
                )}
              </div>

              {/* Mobile 3-dot menu */}
              <div className={s.chatHeaderMobileMenu} style={{ position: 'relative' }}>
                <button
                  onClick={() => setMobileMenuOpen(p => !p)}
                  style={{ background: mobileMenuOpen ? 'var(--bg-elevated)' : 'transparent', border: `1px solid ${mobileMenuOpen ? 'var(--border-hover)' : 'var(--border)'}`, borderRadius: '8px', padding: '0.35rem 0.55rem', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1, letterSpacing: '1px' }}
                >⋮</button>
                {mobileMenuOpen && (
                  <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.4)', zIndex: 100, overflow: 'hidden', minWidth: '160px' }}>
                    {messages.length > 0 && (
                      <button onClick={() => { setMobileMenuOpen(false); openSearch() }} style={{ width: '100%', textAlign: 'left', padding: '0.65rem 1rem', background: 'transparent', border: 'none', borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                        🔍 Search
                      </button>
                    )}
                    {pinnedIds.length > 0 && (
                      <button onClick={() => { setMobileMenuOpen(false); setShowPinned(p => !p) }} style={{ width: '100%', textAlign: 'left', padding: '0.65rem 1rem', background: 'transparent', border: 'none', borderBottom: '1px solid var(--border)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                        📌 Pinned <span style={{ marginLeft: 'auto', fontWeight: 700, color: activeVisual.color }}>{pinnedIds.length}</span>
                      </button>
                    )}
                    {messages.length > 0 && (
                      <button onClick={() => { setMobileMenuOpen(false); void handleClearChat() }} disabled={clearing || isTyping} style={{ width: '100%', textAlign: 'left', padding: '0.65rem 1rem', background: 'transparent', border: 'none', color: 'var(--error)', cursor: 'pointer', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                        🗑 Clear
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Search bar */}
            {searchOpen && (
              <div className={s.searchBar}>
                <div className={s.searchInputWrap} style={{ border: `1px solid ${activeVisual.color}44` }}>
                  <span className={s.searchIcon}>🔍</span>
                  <input
                    ref={searchInputRef}
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') e.shiftKey ? goSearchPrev() : goSearchNext(); if (e.key === 'Escape') closeSearch() }}
                    placeholder="Search messages..."
                    className={s.searchInput}
                  />
                  {searchQuery && (
                    <span className={s.searchCount}>
                      {searchLoading ? '…' : searchResults.length > 0 ? `${searchIdx + 1} / ${searchResults.length}` : 'No results'}
                    </span>
                  )}
                </div>
                {searchResults.length > 1 && (
                  <>
                    <button onClick={goSearchPrev} title="Previous" className={s.searchNavBtn}>↑</button>
                    <button onClick={goSearchNext} title="Next" className={s.searchNavBtn}>↓</button>
                  </>
                )}
                <button onClick={closeSearch} title="Close" className={s.searchCloseBtn}>✕</button>
              </div>
            )}

            {/* Pinned panel */}
            {showPinned && pinnedMessages.length > 0 && (
              <div className={s.pinnedPanel} style={{ maxHeight: '180px', overflowY: 'auto' }}>
                <div className={s.pinnedLabel} style={{ color: activeVisual.color }}>📌 Pinned</div>
                {pinnedMessages.map(msg => (
                  <div
                    key={msg.id}
                    onClick={() => { void navigateToMessage(msg.id); setShowPinned(false) }}
                    style={{ padding: '0.4rem 1.25rem', cursor: 'pointer', borderLeft: `3px solid ${activeVisual.color}`, marginLeft: '0.5rem', marginBottom: '0.25rem', background: 'var(--bg-elevated)', borderRadius: '0 6px 6px 0' }}
                  >
                    <div className={s.pinnedMsgTime}>{msg.senderType === 'user' ? 'You' : activeAgent?.name ?? 'AI'}</div>
                    <div className={s.pinnedMsgText}>{msg.content?.replace(/\*\*([^*]+)\*\*/g, '$1') ?? ''}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Messages */}
            <div ref={messagesAreaRef} className={s.messages}>
              <div ref={topSentinelRef} className={s.topSentinel} />
              {loadingMore && (
                <div className={s.loadingOlder}><div className={s.miniSpinner} />Loading older messages…</div>
              )}
              {!hasMore && messages.length > 0 && (
                <div className={s.beginningLabel}>Beginning of conversation</div>
              )}

              {/* Skeletons */}
              {(loadingMessages || loadingConv) && (
                <div className={s.msgSkeletonList}>
                  {[{ w: '55%', u: false }, { w: '45%', u: true }, { w: '65%', u: false }].map((sk, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: sk.u ? 'flex-end' : 'flex-start', gap: '0.5rem', alignItems: 'flex-end' }}>
                      {!sk.u && <div style={{ width: 30, height: 30, borderRadius: '10px', background: 'var(--bg-elevated)', flexShrink: 0 }} />}
                      <div style={{ height: 44, width: sk.w, background: 'var(--bg-elevated)', borderRadius: '18px', opacity: 0.6 + i * 0.1 }} />
                    </div>
                  ))}
                </div>
              )}

              {/* Welcome */}
              {!loadingMessages && !loadingConv && isEmpty && activeAgent && (
                <WelcomeScreen agentName={activeAgent.name} agentVisual={activeVisual} onPrompt={text => void handleSend(text)} />
              )}

              <div className={s.msgSpacer} />

              {/* Messages with date dividers */}
              {!loadingMessages && !loadingConv && messagesWithDividers.map((item, idx) => {
                if (item.type === 'date') {
                  return <DateDivider key={`date-${idx}`} label={item.label} />
                }
                const { message: msg, showAvatar, showTime } = item
                const isSearchMatch = searchResults.some(r => r.id === msg.id)
                const isActiveMatch = searchResults[searchIdx]?.id === msg.id
                return (
                  <div
                    key={msg.id}
                    ref={el => { messageRefs.current[msg.id] = el }}
                    style={{
                      borderRadius: '12px',
                      outline: isActiveMatch ? `2px solid ${activeVisual.color}88` : isSearchMatch ? `1px solid ${activeVisual.color}44` : 'none',
                      borderLeft: pinnedIds.includes(msg.id) ? `3px solid var(--warning)` : 'none',
                      background: pinnedIds.includes(msg.id) ? 'rgba(234,179,8,0.05)' : 'transparent',
                      paddingLeft: pinnedIds.includes(msg.id) ? '0.35rem' : '0',
                      transition: 'outline 0.2s, background 0.2s',
                    }}
                  >
                    <MessageBubble
                      message={msg}
                      agentVisual={activeVisual}
                      isLastAgent={msg.senderType === 'agent' && showAvatar}
                      showTime={showTime}
                      onPin={() => togglePin(msg.id)}
                      isPinned={pinnedIds.includes(msg.id)}
                      conversationId={conversationId ?? undefined}
                      onUnlocked={(msgId, content) => {
                        setMessages(prev => prev.map(m => m.id === msgId ? { ...m, content, unlocked: true } : m))
                      }}
                      freeUnlocksRemaining={freeUnlocksRemaining}
                      onFreeUnlockUsed={() => setFreeUnlocksRemaining(prev => prev !== null ? Math.max(0, prev - 1) : prev)}
                    />
                  </div>
                )
              })}

              {/* Typing + streaming */}
              {isTyping && <TypingIndicator agentVisual={activeVisual} />}
              {streamingMessage && (
                <MessageBubble
                  key={streamingMessage.streamId}
                  message={{
                    id: streamingMessage.streamId, senderType: 'agent',
                    senderId: activeAgentId ?? '', senderName: activeAgent?.name ?? null,
                    content: streamingMessage.content, isDeleted: false, editedAt: null,
                    createdAt: new Date().toISOString(),
                    isPremium: false, premiumAmountPaise: null, unlocked: null,
                  }}
                  agentVisual={activeVisual}
                  isLastAgent
                  isStreaming
                />
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Scroll to bottom button */}
            {showScrollBtn && (
              <button
                className={s.scrollToBottomBtn}
                onClick={() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })}
                title="Scroll to bottom"
              >↓</button>
            )}

            {/* Error / Spending cap banner */}
            {chatCapReached && (
              <div style={{ margin: '0 1rem 0.75rem', padding: '0.6rem 1rem', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '1rem', flexShrink: 0 }}>⚡</span>
                <span style={{ fontWeight: 700, fontSize: '0.825rem', color: 'var(--text-primary)', flexShrink: 0 }}>{capConfig?.chatBanner.title ?? 'Daily chat limit reached'}</span>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>&middot; Resets at {chatCapResetTime ?? 'midnight'}</span>
              </div>
            )}
            {errorMsg && <div className={s.errorMsg}><span>⚠️</span> {errorMsg}</div>}

            {/* Input or deactivated notice */}
            {agentInactive ? (
              <div style={{
                margin: '0 1rem 1rem',
                padding: '1rem 1.25rem',
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
              }}>
                <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>🚫</span>
                <div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.2rem' }}>
                    {activeAgent?.name} is no longer available
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    This agent has been deactivated. Your conversation history is preserved above.
                  </div>
                </div>
              </div>
            ) : (
            <div className={s.inputArea}>
              <div
                ref={inputContainerRef}
                className={s.inputRow}
                onFocusCapture={e => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = activeVisual.color; el.style.boxShadow = `0 0 0 3px ${activeVisual.color}18` }}
                onBlurCapture={e => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = 'var(--border)'; el.style.boxShadow = 'none' }}
              >
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={chatCapReached ? 'Daily limit reached — come back tomorrow' : agentInactive ? 'This agent is no longer available' : `Message ${activeAgent?.name ?? 'AI'}…`}
                  disabled={chatCapReached || agentInactive}
                  rows={1}
                  className={s.inputBox}
                  style={{ opacity: (chatCapReached || agentInactive) ? 0.5 : 1, cursor: (chatCapReached || agentInactive) ? 'not-allowed' : undefined }}
                  onInput={e => {
                    const el = e.currentTarget as HTMLTextAreaElement
                    el.style.height = 'auto'
                    el.style.height = `${Math.min(el.scrollHeight, 120)}px`
                  }}
                />
                <button
                  onClick={() => void handleSend()}
                  disabled={!input.trim() || sending || isTyping || chatCapReached || agentInactive}
                  style={{
                    width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
                    background: input.trim() && !sending && !isTyping
                      ? `linear-gradient(135deg, ${activeVisual.color}, ${activeVisual.color}cc)`
                      : 'var(--bg-card)',
                    color: input.trim() && !sending && !isTyping ? 'white' : 'var(--text-muted)',
                    cursor: input.trim() && !sending && !isTyping ? 'pointer' : 'default',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.05rem', transition: 'all 0.2s cubic-bezier(0.16,1,0.3,1)',
                    boxShadow: input.trim() && !sending && !isTyping ? `0 4px 14px ${activeVisual.color}55` : 'none',
                    opacity: (input.trim() && !isTyping) || sending ? 1 : 0.35,
                    border: input.trim() && !sending && !isTyping ? 'none' : '1px solid var(--border)',
                  } as React.CSSProperties}
                >
                  {sending
                    ? <div style={{ width: 14, height: 14, border: `2px solid ${activeVisual.color}44`, borderTopColor: activeVisual.color, borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                    : '↑'
                  }
                </button>
              </div>
              <div className={s.inputFooter}>
                <div className={s.inputHint}>Enter to send · Shift+Enter for new line</div>
                <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.25rem', opacity: 0.7 }}>
                  🔒 Private to your account
                </div>
                {input.length > 0 && (
                  <div style={{ fontSize: '0.65rem', color: input.length > 1800 ? 'var(--error)' : input.length > 1500 ? 'var(--warning)' : 'var(--text-muted)', transition: 'color 0.2s' }}>
                    {input.length} / 2000
                  </div>
                )}
              </div>
            </div>
            )}
          </>
        )}
        </div>
      </div>

      {/* ── Right sidebar ── */}
      {activeAgentId && activeAgent && (
        <div className="sidebar-right">
          <aside style={{ width: '100%', paddingTop: '1rem', paddingBottom: '1rem', paddingLeft: '0.75rem', display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflowY: 'auto', gap: '0.75rem' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '1.25rem 1rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {/* Avatar + name */}
          <div className={s.agentInfoAvatar}>
            <div style={{
              width: 72, height: 72, borderRadius: '20px',
              background: activeVisual.bg,
              border: `2px solid ${activeVisual.color}44`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '2rem',
              boxShadow: `0 0 0 6px ${activeVisual.color}10, 0 8px 28px ${activeVisual.color}25`,
            }}>{activeVisual.icon}</div>
            <div>
              <div className={s.agentInfoName}>{activeAgent.name}</div>
              {activeAgent.categories?.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '0.3rem', flexWrap: 'wrap', marginTop: '0.35rem' }}>
                  {activeAgent.categories.slice(0, CATEGORY_BADGE_PREVIEW_MAX).map(cat => (
                    <span key={cat.id} className={s.agentInfoBadge} style={{ background: `${cat.color}18`, color: cat.color, border: `1px solid ${cat.color}33` }}>
                      {cat.icon} {cat.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
            {activeAgent.description && (
              <div className={s.agentInfoDesc}>{activeAgent.description}</div>
            )}
          </div>

          {/* Stats */}
          <div className={s.agentInfoSection}>
            <div className={s.agentInfoSectionLabel}>Session</div>
            <div className={s.agentInfoStatRow}>
              <span className={s.agentInfoStatLabel}>Messages</span>
              <span className={s.agentInfoStatValue}>{messages.length}</span>
            </div>
            <div className={s.agentInfoStatRow}>
              <span className={s.agentInfoStatLabel}>Status</span>
              <span className={s.agentInfoStatValue} style={{ color: 'var(--online-green)' }}>{isTyping ? 'Typing…' : 'Online'}</span>
            </div>
            {pinnedIds.length > 0 && (
              <div className={s.agentInfoStatRow}>
                <span className={s.agentInfoStatLabel}>Pinned</span>
                <span className={s.agentInfoStatValue} style={{ color: 'var(--warning)' }}>{pinnedIds.length}</span>
              </div>
            )}
          </div>

          {/* Try asking */}
          <div className={s.agentInfoSection}>
            <div className={s.agentInfoSectionLabel}>Try asking</div>
            {(AGENT_SUGGESTIONS[activeAgent.name] ?? DEFAULT_SUGGESTIONS).map(prompt => (
              <button
                key={prompt}
                className={s.agentInfoPromptBtn}
                style={{}}
                onMouseEnter={e => { const el = e.currentTarget as HTMLButtonElement; el.style.borderColor = `${activeVisual.color}55`; el.style.background = activeVisual.bg }}
                onMouseLeave={e => { const el = e.currentTarget as HTMLButtonElement; el.style.borderColor = 'var(--border)'; el.style.background = 'var(--bg-elevated)' }}
                onClick={() => void handleSend(prompt)}
              >
                <span style={{ color: activeVisual.color, flexShrink: 0, marginTop: '0.05rem' }}>→</span>
                {prompt}
              </button>
            ))}
          </div>
          </div>
          </aside>
        </div>
      )}

      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  )
}
